'use client';

import useDynamicTranslation from '@/hooks/useDynamicTranslation';
import { Link } from '@/i18n/routing';
import { Article } from '@/types';
import { useLocale } from 'next-intl';
import ArticleItem from '@/components/common/ArticleItem';

interface ArticleClientProps {
  article: Article;
}

export default function ArticleClient({ article }: ArticleClientProps) {
  const { dt } = useDynamicTranslation();
  const locale = useLocale();

  return (
    <div className="page-wrapper" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <ul className="ps-breadcrumb">
        <li className="ps-breadcrumb__item">
          <Link href="/">{dt('home')}</Link>
        </li>
        <li className="ps-breadcrumb__item" aria-current="page">
          <Link href="/articles">{dt('blogs')}</Link>
        </li>
        <li className="ps-breadcrumb__item active" aria-current="page">
          {article.title}
        </li>
      </ul>
      {/* main article */}
      <div className="main-item">
        <img src={article.image} alt="article image" />
        <div className="content">
          <h3>{article.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: article.body }}></div>
        </div>
      </div>

      {/* other articles */}
      <div className="sublist">
        <div className="sublist-header">
          <h3 className="title">{dt('you_may_like')}</h3>
          <Link href="/articles">
            {dt('view_all')}
            <i className="fa fa-arrow-left"></i>
          </Link>
        </div>
        <div className="sublist-items">
          {article?.related_articles?.map((article) => (
            <ArticleItem key={article.id} article={article} />
          ))}
        </div>
      </div>

      <style jsx>{`
        .page-wrapper {
          padding: 40px 20px;
          max-width: 1500px;
          margin: 0 auto;
        }

        .main-item {
          background-color: #ffffff;
          border-radius: 10px;
          text-align: start;
          overflow: hidden;
        }

        .main-item img {
          width: 100%;
          object-fit: contain;
        }

        .main-item .content {
          padding: 40px;
        }

        .main-article .content h3 {
          margin-bottom: 20px;
        }

        .sublist-header {
          margin-top: 8rem;
          padding: 10px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .sublist-header h3 {
          text-transform: capitalize;
          color: var(--body-text-color);
        }

        .sublist-header a {
          color: var(--second-color);
          display: flex;
          align-items: center;
          font-weight: 600;
        }

        .sublist-header a i {
          margin: 0 6px
        }

        [dir=ltr] .sublist-header a i {
          transform: scale(-1);
        }

        .sublist-items {
          display: grid;
          grid-template-columns: 1fr;
          gap: 30px;
        }

        @media (min-width: 768px) {
          .sublist-items {
            grid-template-columns: 1fr 1fr;
          }

          .main-item img {
            object-fit: cover;
            height:600px
          }
        }

        @media (min-width: 1024px) {
          .sublist-items {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }

        @media (min-width: 1280px) {
          .sublist-items {
            grid-template-columns: 1fr 1fr 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
